import React, {
  useEffect,
} from 'react';
import {
  FieldError,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import {
  PatientDTO,
} from '@/dtos';
import Controller from 'core/components/FormController';
import {
  Dayjs,
} from 'utils/dayjs';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Input from 'components/Input';
import Wrapper from 'components/Input/Wrapper';
import Select from 'core/components/Select';
import {
  validateDateIfFuture,
  validatePatientName,
} from 'utils/helper';

export const genders = ['Male', 'Female', 'Other'];

export type DemographicsForm = Pick<PatientDTO,
 'firstName'
  | 'middleName'
  | 'lastName'
  | 'genderDescription'
> & { dateOfBirth: Dayjs };

type Props = {
  value?: DemographicsForm | null;
  onChange: (value: DemographicsForm | null) => void;
  disabled?: boolean;
  error?: FieldError;
};
export default function DemographicFields({
  value,
  onChange,
  disabled,
  error,
}: Props) {
  const {
    control,
    watch,
    reset,
    formState: { isDirty },
    trigger,
  } = useForm<DemographicsForm>({
    mode: 'onChange',
    defaultValues: value ?? {},
  });

  const formValue = watch();
  useEffect(() => {
    if (isDirty) {
      reset(formValue, { keepErrors: true });
      onChange(formValue);
    }
  }, [formValue, isDirty]);

  useEffect(() => {
    if (!isNil(error)) {
      trigger();
    }
  }, [error]);

  return (
    <div className="flex gap-3 w-full">
      <Controller
        name="firstName"
        rules={{
          validate: (value) => validatePatientName(value, 'Enter a valid first name'),
        }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            error={!isNil(error)}
            showErrorText={false}
            disabled={disabled}
            className="w-1/5"
            id="first-name"
            label="First Name"
            type="text"
            inputProps={{ autoComplete: 'off' }}
          />
        )}
      />
      <Controller
        name="middleName"
        control={control}
        rules={{
          validate: (value) => validatePatientName(value, 'Enter a valid middle name', false),
        }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            error={!isNil(error)}
            showErrorText={false}
            disabled={disabled}
            className="w-1/5"
            id="insurance-pater"
            label="Middle Name"
            type="text"
            inputProps={{ autoComplete: 'off' }}
          />
        )}
      />
      <Controller
        name="lastName"
        rules={{
          validate: (value) => validatePatientName(value, 'Enter a valid last name'),
        }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            disabled={disabled}
            error={!isNil(error)}
            showErrorText={false}
            className="w-1/5"
            id="insurance-last-name"
            inputProps={{ autoComplete: 'off' }}
            label="Last Name"
            type="text"
          />
        )}
      />
      <Controller
        name="dateOfBirth"
        rules={{ required: true, validate: validateDateIfFuture }}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Wrapper
            id="insured-date-of-birth"
            label="Date of birth"
            className="w-[calc(20%-8px)]"
            error={!isNil(error)}
            showErrorText={false}
          >
            <DatePicker
              {...field}
              disabled={disabled}
              containerClassName="w-full"
              className="w-full"
              disableFuture
              showDateButtons={false}
            />
          </Wrapper>
        )}
      />
      <Controller
        name="genderDescription"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Select<string>
            {...field}
            id="gender"
            disabled={disabled}
            className="w-[calc(20%-8px)] text-gray-500 text-xs"
            label="Gender"
            placeholder="Select"
            options={genders}
            getLabel={(option: string) => option}
            getValue={(option: string) => option}
          />
        )}
      />
    </div>
  );
}
